import { LabelPreferenceId } from './labelPreferencesIds';
import type { MailRibbonConfigStore } from '../types/mailRibbonConfigStore';
import type { MailRibbonGroup } from '../types/mailRibbonGroup';
import type { RibbonControlId } from 'owa-ribbon-ids';
import {
    ActionControlId,
    MailRibbonTabId,
    MailRibbonGroupId,
    MailRibbonControlId,
    FormatControlId,
} from 'owa-ribbon-ids';

// This variable should always match the length of `mailRibbonVersionManager` in owa-mail-ribbon-versioning,
// but we can't import that length due to circular dependencies. Be sure to increment when updating versioning.
const CURRENT_RIBBON_VERSION: number = 1;

function getRibbonSectionContents(
    groupId: MailRibbonGroupId,
    controlIds: RibbonControlId[]
): MailRibbonGroup {
    return {
        groupId,
        groupName: undefined,
        controlIds,
    };
}

/**
 * This is the default configuration for OWA/Monarch's ribbon. Should there be no user
 * customization, this ribbon will be used as is.
 *
 * Important: If you make changes to defaultConfig, you will likely want to make changes
 * to Ribbon Versioning too such that users with a customized ribbon will grab your changes.
 * @see {mailRibbonVersionManager} and please add what you've changed there.
 */
export const defaultConfig: MailRibbonConfigStore = {
    version: CURRENT_RIBBON_VERSION,
    multiline: {
        homeTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_ToggleLeftPane, [
                    MailRibbonControlId.ToggleLeftPane,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_New, [
                    MailRibbonControlId.NewMessage,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Delete, [
                    MailRibbonControlId.IgnoreStopIgnore,
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.DeleteItem,
                    MailRibbonControlId.Archive,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Respond, [
                    MailRibbonControlId.RespondReply,
                    MailRibbonControlId.RespondReplyAll,
                    MailRibbonControlId.RespondForward,
                    MailRibbonControlId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_QuickSteps, [
                    MailRibbonControlId.QuickStepsGallery,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Move, [
                    MailRibbonControlId.Sweep,
                    MailRibbonControlId.Move,
                    MailRibbonControlId.Rules,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.Categorize,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.PinUnpin,
                    MailRibbonControlId.Snooze,
                    MailRibbonControlId.AssignPolicy,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Find, [
                    MailRibbonControlId.AddressBook,
                    MailRibbonControlId.BrowseGroups,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Speech, [
                    MailRibbonControlId.ReadAloud,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Undo, [MailRibbonControlId.Undo]),
                getRibbonSectionContents(MailRibbonGroupId.Group_AddIns, [
                    ActionControlId.GetAddIns,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Insights, [
                    MailRibbonControlId.Insights,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Protection, [
                    MailRibbonControlId.ReportMessage,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_ToggleLeftPane,
                MailRibbonGroupId.Group_New,
                MailRibbonGroupId.Group_Delete,
                MailRibbonGroupId.Group_Respond,
                MailRibbonGroupId.Group_QuickSteps,
                MailRibbonGroupId.Group_Move,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Find,
                MailRibbonGroupId.Group_Speech,
                MailRibbonGroupId.Group_Undo,
                MailRibbonGroupId.Group_AddIns,
                MailRibbonGroupId.Group_Insights,
                MailRibbonGroupId.Group_Protection,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        viewTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Settings, [
                    MailRibbonControlId.ViewSettings,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Messages, [
                    MailRibbonControlId.Conversations,
                    MailRibbonControlId.MessagePreview,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Layout, [
                    MailRibbonControlId.FolderPane,
                    MailRibbonControlId.ReadingPane,
                    MailRibbonControlId.Density,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_ImmersiveReader, [
                    MailRibbonControlId.ImmersiveReader,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Print, [
                    MailRibbonControlId.Print,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_View_Settings,
                MailRibbonGroupId.Group_View_Messages,
                MailRibbonGroupId.Group_View_Layout,
                MailRibbonGroupId.Group_ImmersiveReader,
                MailRibbonGroupId.Group_Print,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        insertTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Include, [
                    MailRibbonControlId.AttachFile,
                    MailRibbonControlId.AttachSig,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tables, [
                    ActionControlId.InsertTable,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Illustrations, [
                    MailRibbonControlId.Pictures,
                    MailRibbonControlId.Emojis,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Links, [
                    MailRibbonControlId.ShowLinkDialog,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Include,
                MailRibbonGroupId.Group_Tables,
                MailRibbonGroupId.Group_Illustrations,
                MailRibbonGroupId.Group_Links,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        formatTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Clipboard_Format, [
                    FormatControlId.Paste,
                    FormatControlId.Cut,
                    FormatControlId.Copy,
                    FormatControlId.FormatPainter,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_BasicText, [
                    FormatControlId.FontNamePicker,
                    FormatControlId.FontSizePicker,
                    FormatControlId.ChangeCase,
                    FormatControlId.RemoveFormat,
                    FormatControlId.Bold,
                    FormatControlId.Italic,
                    FormatControlId.Underline,
                    FormatControlId.StrikeThrough,
                    FormatControlId.Superscript,
                    FormatControlId.Subscript,
                    FormatControlId.FontBackColorPicker,
                    FormatControlId.FontColorPicker,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Paragraph, [
                    FormatControlId.Bullets,
                    FormatControlId.Numbering,
                    FormatControlId.Outdent,
                    FormatControlId.Indent,
                    FormatControlId.AlignLeft,
                    FormatControlId.AlignCenter,
                    FormatControlId.AlignRight,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Clipboard_Format,
                MailRibbonGroupId.Group_BasicText,
                MailRibbonGroupId.Group_Paragraph,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Clipboard_Message, [
                    MailRibbonControlId.Paste,
                    MailRibbonControlId.FormatPainter,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Names, [
                    MailRibbonControlId.AddressBook,
                    MailRibbonControlId.CheckNames,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Include, [
                    MailRibbonControlId.AttachFile,
                    MailRibbonControlId.AttachSig,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Sensitivity, [
                    ActionControlId.Sensitivity,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.HighImportance,
                    MailRibbonControlId.LowImportance,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.AssignPolicy,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Voice, [
                    MailRibbonControlId.Dictate,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_ImmersiveReader, [
                    MailRibbonControlId.ImmersiveReader,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Speech, [
                    MailRibbonControlId.ReadAloud,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Encrypt, [
                    MailRibbonControlId.Encrypt,
                    MailRibbonControlId.Encrypt_SingleButton,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Save, [MailRibbonControlId.Save]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Clipboard_Message,
                MailRibbonGroupId.Group_Names,
                MailRibbonGroupId.Group_Include,
                MailRibbonGroupId.Group_Sensitivity,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Voice,
                MailRibbonGroupId.Group_ImmersiveReader,
                MailRibbonGroupId.Group_Speech,
                MailRibbonGroupId.Group_Encrypt,
                MailRibbonGroupId.Group_Save,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        notesTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Notes_New, [
                    MailRibbonControlId.NewMessage,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Notes_Edit, [
                    MailRibbonControlId.Notes_DeleteNote,
                    MailRibbonControlId.Notes_ChangeColor,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Notes_View, [
                    MailRibbonControlId.Notes_ViewEmail,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Notes_New,
                MailRibbonGroupId.Group_Notes_Edit,
                MailRibbonGroupId.Group_Notes_View,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        tableFormatTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Structure, [
                    FormatControlId.DeleteTableMenu,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Merge_Split, [
                    FormatControlId.TableMergeCellsMenu,
                    FormatControlId.TableSplitCellsMenu,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Insert, [
                    FormatControlId.InsertRowAbove,
                    FormatControlId.InsertRowBelow,
                    FormatControlId.InsertColumnLeft,
                    FormatControlId.InsertColumnRight,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Align, [
                    FormatControlId.TableAlignCellHorizontallyMenu,
                    FormatControlId.TableAlignCellVerticallyMenu,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Table_Structure,
                MailRibbonGroupId.Group_Table_Design,
                MailRibbonGroupId.Group_Table_Merge_Split,
                MailRibbonGroupId.Group_Table_Insert,
                MailRibbonGroupId.Group_Table_Align,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        readTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Delete, [
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.DeleteItem,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Respond, [
                    MailRibbonControlId.RespondReply,
                    MailRibbonControlId.RespondReplyAll,
                    MailRibbonControlId.RespondForward,
                    MailRibbonControlId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Move, [MailRibbonControlId.Rules]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.FlagUnflag,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Print, [
                    MailRibbonControlId.Print,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Delete,
                MailRibbonGroupId.Group_Respond,
                MailRibbonGroupId.Group_Move,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Print,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
    },
    singleline: {
        homeTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Move_and_Delete, [
                    MailRibbonControlId.ToggleLeftPane,
                    MailRibbonControlId.NewMessage,
                    MailRibbonControlId.DeleteItem,
                    MailRibbonControlId.Archive,
                    MailRibbonControlId.Sweep,
                    MailRibbonControlId.Move,
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.Rules,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Respond, [
                    MailRibbonControlId.RespondReply,
                    MailRibbonControlId.RespondReplyAll,
                    MailRibbonControlId.RespondForward,
                    MailRibbonControlId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.Categorize,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.PinUnpin,
                    MailRibbonControlId.Snooze,
                    MailRibbonControlId.AssignPolicy,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Find, [
                    MailRibbonControlId.AddressBook,
                    MailRibbonControlId.BrowseGroups,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Undo, [MailRibbonControlId.Undo]),
                getRibbonSectionContents(MailRibbonGroupId.Group_AddIns, [
                    ActionControlId.GetAddIns,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_RibbonCustomizer, [
                    MailRibbonControlId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                MailRibbonControlId.BrowseGroups,
                MailRibbonControlId.JunkFlyout,
                MailRibbonControlId.ReplyWithMeeting,
                MailRibbonControlId.Rules,
            ],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Move_and_Delete,
                MailRibbonGroupId.Group_Respond,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Undo,
                MailRibbonGroupId.Group_Find,
                MailRibbonGroupId.Group_AddIns,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        viewTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Settings, [
                    MailRibbonControlId.ViewSettings,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Messages, [
                    MailRibbonControlId.Conversations,
                    MailRibbonControlId.MessagePreview,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_View_Layout, [
                    MailRibbonControlId.Layout,
                    MailRibbonControlId.Density,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_ImmersiveReader, [
                    MailRibbonControlId.ImmersiveReader,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Print, [
                    MailRibbonControlId.Print,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_RibbonCustomizer, [
                    MailRibbonControlId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_View_Settings,
                MailRibbonGroupId.Group_View_Messages,
                MailRibbonGroupId.Group_View_Layout,
                MailRibbonGroupId.Group_ImmersiveReader,
                MailRibbonGroupId.Group_Print,
                MailRibbonGroupId.Group_RibbonCustomizer,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        insertTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Include, [
                    MailRibbonControlId.AttachFile,
                    MailRibbonControlId.AttachSig,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tables, [
                    ActionControlId.InsertTable,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Illustrations, [
                    MailRibbonControlId.Pictures,
                    MailRibbonControlId.Emojis,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Links, [
                    MailRibbonControlId.ShowLinkDialog,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Include,
                MailRibbonGroupId.Group_Tables,
                MailRibbonGroupId.Group_Illustrations,
                MailRibbonGroupId.Group_Links,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        formatTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Clipboard_Format, [
                    FormatControlId.UndoRedo,
                    FormatControlId.PasteCutCopy,
                    FormatControlId.FormatPainter,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_BasicText, [
                    FormatControlId.FontNamePicker,
                    FormatControlId.FontSizePicker,
                    FormatControlId.Bold,
                    FormatControlId.Italic,
                    FormatControlId.Underline,
                    FormatControlId.FontBackColorPicker,
                    FormatControlId.FontColorPicker,
                    FormatControlId.RemoveFormat,
                    FormatControlId.ChangeCase,
                    FormatControlId.StrikeThrough,
                    FormatControlId.Superscript,
                    FormatControlId.Subscript,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Paragraph, [
                    FormatControlId.Bullets,
                    FormatControlId.Numbering,
                    FormatControlId.Indent,
                    FormatControlId.Outdent,
                    FormatControlId.AlignLeft,
                    FormatControlId.AlignCenter,
                    FormatControlId.AlignRight,
                    FormatControlId.LeftToRight,
                    FormatControlId.RightToLeft,
                    FormatControlId.Quote,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Clipboard_Format,
                MailRibbonGroupId.Group_BasicText,
                MailRibbonGroupId.Group_Paragraph,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Clipboard_Message, [
                    MailRibbonControlId.Paste,
                    MailRibbonControlId.FormatPainter,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Names, [
                    MailRibbonControlId.AddressBook,
                    MailRibbonControlId.CheckNames,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Include, [
                    MailRibbonControlId.AttachFile,
                    MailRibbonControlId.AttachSig,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Sensitivity, [
                    ActionControlId.Sensitivity,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.HighImportance,
                    MailRibbonControlId.LowImportance,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.AssignPolicy,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Voice, [
                    MailRibbonControlId.Dictate,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_ImmersiveReader, [
                    MailRibbonControlId.ImmersiveReader,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Speech, [
                    MailRibbonControlId.ReadAloud,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Encrypt, [
                    MailRibbonControlId.Encrypt,
                    MailRibbonControlId.Encrypt_SingleButton,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Save, [MailRibbonControlId.Save]),
                getRibbonSectionContents(MailRibbonGroupId.Group_RibbonCustomizer, [
                    MailRibbonControlId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [MailRibbonControlId.Save],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Clipboard_Message,
                MailRibbonGroupId.Group_Names,
                MailRibbonGroupId.Group_Include,
                MailRibbonGroupId.Group_Sensitivity,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Voice,
                MailRibbonGroupId.Group_ImmersiveReader,
                MailRibbonGroupId.Group_Speech,
                MailRibbonGroupId.Group_Encrypt,
                MailRibbonGroupId.Group_Save,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        notesTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Notes_Edit, [
                    MailRibbonControlId.NewMessage,
                    MailRibbonControlId.Notes_DeleteNote,
                    MailRibbonControlId.Notes_ChangeColor,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Notes_View, [
                    MailRibbonControlId.Notes_ViewEmail,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Notes_Edit,
                MailRibbonGroupId.Group_Notes_View,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        tableFormatTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Structure, [
                    FormatControlId.DeleteTableMenu,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Merge_Split, [
                    FormatControlId.TableMergeCellsMenu,
                    FormatControlId.TableSplitCellsMenu,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Insert, [
                    FormatControlId.InsertRowAbove,
                    FormatControlId.InsertRowBelow,
                    FormatControlId.InsertColumnLeft,
                    FormatControlId.InsertColumnRight,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Table_Align, [
                    FormatControlId.TableAlignCellHorizontallyMenu,
                    FormatControlId.TableAlignCellVerticallyMenu,
                ]),
            ],
            controlsInOverflow: [],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Table_Structure,
                MailRibbonGroupId.Group_Table_Design,
                MailRibbonGroupId.Group_Table_Merge_Split,
                MailRibbonGroupId.Group_Table_Insert,
                MailRibbonGroupId.Group_Table_Align,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
        readTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Move_and_Delete, [
                    MailRibbonControlId.DeleteItem,
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.Rules,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Respond, [
                    MailRibbonControlId.RespondReply,
                    MailRibbonControlId.RespondReplyAll,
                    MailRibbonControlId.RespondForward,
                    MailRibbonControlId.ReplyWithMeeting,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Tags, [
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.FlagUnflag,
                ]),
                getRibbonSectionContents(MailRibbonGroupId.Group_Print, [
                    MailRibbonControlId.Print,
                ]),
            ],
            controlsInOverflow: [
                MailRibbonControlId.JunkFlyout,
                MailRibbonControlId.ReplyWithMeeting,
                MailRibbonControlId.Rules,
            ],
            staticGroupIdOrdering: [
                MailRibbonGroupId.Group_Move_and_Delete,
                MailRibbonGroupId.Group_Respond,
                MailRibbonGroupId.Group_Tags,
                MailRibbonGroupId.Group_Print,
            ],
            showLabelsPreference: LabelPreferenceId.ShowAsSpacePermits,
            removedDefaultGroups: [],
        },
    },
    toolbar: {
        homeTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Toolbar_Home, [
                    MailRibbonControlId.ToggleLeftPane,
                    MailRibbonControlId.NewMessage,
                    MailRibbonControlId.DeleteItem,
                    MailRibbonControlId.Archive,
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.Sweep,
                    MailRibbonControlId.Move,
                    MailRibbonControlId.Categorize,
                    MailRibbonControlId.Snooze,
                    MailRibbonControlId.Undo,
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.PinUnpin,
                    MailRibbonControlId.IgnoreStopIgnore,
                    MailRibbonControlId.CreateRule,
                    MailRibbonControlId.Print,
                    MailRibbonControlId.ImmersiveReader,
                    MailRibbonControlId.AssignPolicy,
                    MailRibbonControlId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                MailRibbonControlId.AssignPolicy,
                MailRibbonControlId.CreateRule,
                MailRibbonControlId.FlagUnflag,
                MailRibbonControlId.IgnoreStopIgnore,
                MailRibbonControlId.ImmersiveReader,
                MailRibbonControlId.PinUnpin,
                MailRibbonControlId.Print,
                MailRibbonControlId.ReadUnread,
                MailRibbonControlId.RibbonCustomizer,
            ],
            staticGroupIdOrdering: [MailRibbonGroupId.Group_Toolbar_Home],
            showLabelsPreference: LabelPreferenceId.AlwaysShow,
            removedDefaultGroups: [],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_Toolbar_Message, [
                    MailRibbonControlId.ToggleLeftPane,
                    MailRibbonControlId.NewMessage,
                    MailRibbonControlId.SendDraft,
                    MailRibbonControlId.AttachFile,
                    ActionControlId.Sensitivity,
                    MailRibbonControlId.Encrypt_SingleButton,
                    MailRibbonControlId.DiscardDraft,
                    MailRibbonControlId.Save,
                    MailRibbonControlId.AttachSig,
                    MailRibbonControlId.ShowHideFrom,
                    MailRibbonControlId.Encrypt,
                    MailRibbonControlId.SetImportance,
                    MailRibbonControlId.ShowMessageOptions,
                    MailRibbonControlId.SwitchDraftFormat,
                    MailRibbonControlId.CheckForAccessibilityIssues,
                    MailRibbonControlId.RibbonCustomizer,
                ]),
            ],
            controlsInOverflow: [
                MailRibbonControlId.AttachSig,
                MailRibbonControlId.CheckForAccessibilityIssues,
                MailRibbonControlId.Encrypt,
                MailRibbonControlId.SetImportance,
                MailRibbonControlId.RibbonCustomizer,
                MailRibbonControlId.Save,
                MailRibbonControlId.ShowHideFrom,
                MailRibbonControlId.ShowMessageOptions,
                MailRibbonControlId.SwitchDraftFormat,
            ],
            staticGroupIdOrdering: [MailRibbonGroupId.Group_Toolbar_Message],
            showLabelsPreference: LabelPreferenceId.AlwaysShow,
            removedDefaultGroups: [],
        },
        readonlyTab: {
            layout: [
                getRibbonSectionContents(MailRibbonGroupId.Group_ReadOnly_Toolbar, [
                    MailRibbonControlId.Respond,
                    MailRibbonControlId.DeleteItem,
                    MailRibbonControlId.JunkFlyout,
                    MailRibbonControlId.ReadUnread,
                    MailRibbonControlId.FlagUnflag,
                    MailRibbonControlId.CreateRule,
                    MailRibbonControlId.Print,
                ]),
            ],
            controlsInOverflow: [
                MailRibbonControlId.ReadUnread,
                MailRibbonControlId.FlagUnflag,
                MailRibbonControlId.CreateRule,
                MailRibbonControlId.Print,
            ],
            staticGroupIdOrdering: [MailRibbonGroupId.Group_Toolbar_Message],
            showLabelsPreference: LabelPreferenceId.AlwaysShow,
            removedDefaultGroups: [],
        },
    },
    selectedTab: MailRibbonTabId.Tab_Home,
};
